import { useState, useEffect, useRef } from 'react';
import { apiRequest, getImageUrl } from '../../utils/api';

const VoteFormModal = ({ isOpen, onClose, vote, organizationId, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: Basic info, 2: Product selection, 3: Discount settings
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        voting_duration_minutes: 60,
        campaign_end_date: '',
        show_live_results: true
    });
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]); // Array of {product_id, discount_price, discount_percentage}
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        loadProducts();
    }, [organizationId]);

    useEffect(() => {
        if (vote) {
            setFormData({
                title: vote.title || '',
                description: vote.description || '',
                voting_duration_minutes: vote.voting_duration_minutes || 60,
                campaign_end_date: vote.campaign_end_date ? vote.campaign_end_date.split('T')[0] + 'T' + vote.campaign_end_date.split('T')[1]?.substring(0, 5) : '',
                show_live_results: vote.show_live_results !== false
            });
            if (vote.options) {
                setSelectedProducts(vote.options.map(opt => ({
                    product_id: opt.product_id,
                    product: {
                        id: opt.product_id,
                        name: opt.product_name,
                        price: opt.original_price,
                        currency: opt.currency,
                        image: opt.product_image
                    },
                    discount_price: opt.discount_price || '',
                    discount_percentage: opt.discount_percentage || ''
                })));
            }
        } else {
            resetForm();
        }
    }, [vote, isOpen]);

    useEffect(() => {
        if (searchQuery) {
            const filtered = products.filter(p => 
                p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.description?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts(products);
        }
    }, [searchQuery, products]);

    const loadProducts = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/products`);
            setProducts(response.data || []);
            setFilteredProducts(response.data || []);
        } catch (error) {
            console.error('Load products error:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            voting_duration_minutes: 60,
            campaign_end_date: '',
            show_live_results: true
        });
        setSelectedProducts([]);
        setSearchQuery('');
        setError(null);
        setStep(1);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const isProductSelected = (productId) => {
        return selectedProducts.some(p => p.product_id === productId);
    };

    const handleProductToggle = (product) => {
        if (isProductSelected(product.id)) {
            setSelectedProducts(prev => prev.filter(p => p.product_id !== product.id));
        } else {
            if (selectedProducts.length >= 12) {
                setError('En fazla 12 ürün seçebilirsiniz');
                return;
            }
            const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
            setSelectedProducts(prev => [...prev, {
                product_id: product.id,
                product: {
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    currency: product.currency || 'TRY',
                    image: primaryImage?.image_url
                },
                discount_price: '',
                discount_percentage: ''
            }]);
        }
    };

    const handleDiscountChange = (productId, field, value) => {
        setSelectedProducts(prev => prev.map(p => {
            if (p.product_id !== productId) return p;
            
            const updated = { ...p, [field]: value };
            
            // Auto-calculate discount percentage if price entered
            if (field === 'discount_price' && value && p.product?.price) {
                const originalPrice = parseFloat(p.product.price);
                const discountPrice = parseFloat(value);
                if (discountPrice < originalPrice) {
                    updated.discount_percentage = Math.round((1 - discountPrice / originalPrice) * 100);
                }
            }
            
            // Auto-calculate discount price if percentage entered
            if (field === 'discount_percentage' && value && p.product?.price) {
                const originalPrice = parseFloat(p.product.price);
                const percentage = parseInt(value);
                if (percentage > 0 && percentage <= 100) {
                    updated.discount_price = (originalPrice * (1 - percentage / 100)).toFixed(2);
                }
            }
            
            return updated;
        }));
    };

    const handleRemoveProduct = (productId) => {
        setSelectedProducts(prev => prev.filter(p => p.product_id !== productId));
    };

    const handleSubmit = async () => {
        if (selectedProducts.length < 2) {
            setError('En az 2 ürün seçmelisiniz');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = vote
                ? `/organizations/${organizationId}/votes/${vote.id}`
                : `/organizations/${organizationId}/votes`;

            const payload = {
                ...formData,
                products: selectedProducts.map((p, idx) => ({
                    product_id: p.product_id,
                    discount_price: p.discount_price ? parseFloat(p.discount_price) : null,
                    discount_percentage: p.discount_percentage ? parseInt(p.discount_percentage) : null,
                    sort_order: idx
                }))
            };

            await apiRequest(url, {
                method: vote ? 'PUT' : 'POST',
                body: JSON.stringify(payload)
            });

            onSuccess();
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message || 'Oylama kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        if (step === 1) {
            if (!formData.title) {
                setError('Oylama başlığı gereklidir');
                return;
            }
            setError(null);
            setStep(2);
        } else if (step === 2) {
            if (selectedProducts.length < 2) {
                setError('En az 2 ürün seçmelisiniz');
                return;
            }
            setError(null);
            setStep(3);
        }
    };

    const handleBack = () => {
        setError(null);
        setStep(step - 1);
    };

    const formatDurationDisplay = (minutes) => {
        if (!minutes) return '';
        if (minutes < 60) return `${minutes} dakika`;
        if (minutes < 1440) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return mins > 0 ? `${hours} saat ${mins} dk` : `${hours} saat`;
        }
        const days = Math.floor(minutes / 1440);
        const hours = Math.floor((minutes % 1440) / 60);
        return hours > 0 ? `${days} gün ${hours} saat` : `${days} gün`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                {vote ? '🗳️ Oylama Düzenle' : '🗳️ Yeni Oylama Oluştur'}
                            </h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Adım {step}/3 - {step === 1 ? 'Temel Bilgiler' : step === 2 ? 'Ürün Seçimi' : 'İndirim Ayarları'}
                            </p>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg transition">
                            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Step Indicator */}
                    <div className="flex items-center justify-center gap-2 mt-6">
                        {[1, 2, 3].map((s) => (
                            <div key={s} className="flex items-center">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                                    s === step ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white scale-110' :
                                    s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                                }`}>
                                    {s < step ? '✓' : s}
                                </div>
                                {s < 3 && (
                                    <div className={`w-12 h-1 mx-1 rounded ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                        <span>⚠️</span> {error}
                    </div>
                )}

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
                    {/* Step 1: Basic Info */}
                    {step === 1 && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Oylama Başlığı *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Örn: Bu Haftanın Favori Kokteyli"
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Açıklama (Opsiyonel)
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Oylamanın açıklaması..."
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        ⏱️ Oylama Süresi (Dakika)
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="voting_duration_minutes"
                                            value={formData.voting_duration_minutes}
                                            onChange={handleChange}
                                            min="1"
                                            max="10080"
                                            placeholder="60"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                                            dk
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {formData.voting_duration_minutes > 0 
                                            ? `≈ ${formatDurationDisplay(formData.voting_duration_minutes)}` 
                                            : 'Oylamayı başlattıktan sonra bu süre boyunca oy toplanacak'}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        🎉 Kampanya Bitiş Tarihi
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="campaign_end_date"
                                        value={formData.campaign_end_date}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Kazanan ürünün indirimli olacağı son tarih
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl">
                                <input
                                    type="checkbox"
                                    id="show_live_results"
                                    name="show_live_results"
                                    checked={formData.show_live_results}
                                    onChange={handleChange}
                                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <label htmlFor="show_live_results" className="flex-1">
                                    <span className="font-medium text-gray-800">Canlı sonuçları göster</span>
                                    <p className="text-xs text-gray-500">
                                        Oy kullandıktan sonra kullanıcılar anlık sonuçları görebilir
                                    </p>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Product Selection */}
                    {step === 2 && (
                        <div className="space-y-4">
                            {/* Search */}
                            <div className="relative">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="🔍 Ürün ara..."
                                    className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition"
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>

                            {/* Selected count */}
                            <div className="flex items-center justify-between px-2">
                                <span className="text-sm text-gray-500">
                                    <span className="font-bold text-purple-600">{selectedProducts.length}</span> / 12 ürün seçildi
                                </span>
                                {selectedProducts.length >= 2 && (
                                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">✓ Minimum ürün sayısına ulaşıldı</span>
                                )}
                            </div>

                            {/* Product Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-2">
                                {filteredProducts.map((product) => {
                                    const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
                                    const isSelected = isProductSelected(product.id);
                                    
                                    return (
                                        <div
                                            key={product.id}
                                            onClick={() => handleProductToggle(product)}
                                            className={`relative p-3 border-2 rounded-xl cursor-pointer transition-all ${
                                                isSelected
                                                    ? 'border-purple-500 bg-purple-50 shadow-lg scale-[1.02]'
                                                    : 'border-gray-200 hover:border-purple-300 hover:shadow'
                                            }`}
                                        >
                                            {/* Selection indicator */}
                                            {isSelected && (
                                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white text-xs shadow-lg">
                                                    ✓
                                                </div>
                                            )}

                                            {/* Image */}
                                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 mb-2">
                                                {primaryImage ? (
                                                    <img
                                                        src={getImageUrl(primaryImage.image_url)}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-3xl text-gray-300">
                                                        🍽️
                                                    </div>
                                                )}
                                            </div>

                                            {/* Info */}
                                            <h4 className="font-medium text-gray-800 text-sm truncate">{product.name}</h4>
                                            <p className="text-xs text-purple-600 font-semibold">{parseFloat(product.price).toFixed(2)} {product.currency || 'TRY'}</p>
                                        </div>
                                    );
                                })}
                            </div>

                            {filteredProducts.length === 0 && (
                                <div className="text-center py-10 text-gray-500">
                                    <div className="text-4xl mb-2">🔍</div>
                                    <p>Ürün bulunamadı</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Discount Settings */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">💡</span>
                                    <div>
                                        <h4 className="font-medium text-amber-800">İndirim Bilgisi</h4>
                                        <p className="text-sm text-amber-700">
                                            Her ürün için indirim oranı veya fiyatı belirleyebilirsiniz. 
                                            Kazanan ürün belirlenen indirimle kampanya süresince satışa sunulacak.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {selectedProducts.map((item, idx) => (
                                    <div 
                                        key={item.product_id}
                                        className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                                    >
                                        <div className="flex items-start gap-4">
                                            {/* Product Image */}
                                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                                {item.product?.image ? (
                                                    <img
                                                        src={getImageUrl(item.product.image)}
                                                        alt={item.product.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-2xl text-gray-300">
                                                        🍽️
                                                    </div>
                                                )}
                                            </div>

                                            {/* Product Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-800">{item.product?.name}</h4>
                                                        <p className="text-sm text-gray-500">
                                                            Normal fiyat: <span className="font-medium">{parseFloat(item.product?.price || 0).toFixed(2)} {item.product?.currency || 'TRY'}</span>
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveProduct(item.product_id)}
                                                        className="p-1 text-gray-400 hover:text-red-500 transition"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                {/* Discount Fields */}
                                                <div className="grid grid-cols-2 gap-3 mt-3">
                                                    <div>
                                                        <label className="text-xs text-gray-500 mb-1 block">İndirimli Fiyat</label>
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max={item.product?.price}
                                                                value={item.discount_price}
                                                                onChange={(e) => handleDiscountChange(item.product_id, 'discount_price', e.target.value)}
                                                                placeholder="0.00"
                                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                                            />
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                                                                {item.product?.currency || 'TRY'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-gray-500 mb-1 block">İndirim Oranı</label>
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                value={item.discount_percentage}
                                                                onChange={(e) => handleDiscountChange(item.product_id, 'discount_percentage', e.target.value)}
                                                                placeholder="0"
                                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                                            />
                                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Calculated savings */}
                                                {item.discount_price && item.product?.price && (
                                                    <div className="mt-2 text-xs">
                                                        <span className="text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                            💰 Tasarruf: {(parseFloat(item.product.price) - parseFloat(item.discount_price)).toFixed(2)} {item.product?.currency || 'TRY'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 bg-gray-50">
                    <div className="flex gap-3">
                        {step > 1 && (
                            <button
                                onClick={handleBack}
                                className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-white transition font-medium"
                            >
                                ← Geri
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-white transition font-medium"
                        >
                            İptal
                        </button>
                        <div className="flex-1"></div>
                        {step < 3 ? (
                            <button
                                onClick={handleNext}
                                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition font-medium shadow-lg"
                            >
                                Devam →
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={loading || selectedProducts.length < 2}
                                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Kaydediliyor...
                                    </>
                                ) : (
                                    <>✓ {vote ? 'Güncelle' : 'Oluştur'}</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VoteFormModal;
